import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, Download, RefreshCw, Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";


interface AIImageGeneratorProps {
  eventName: string;
  eventType: string;
  description?: string;
  eventDate?: string;
  eventTime?: string;
  eventLocation?: string;
  hostName?: string;
  deadline?: string;
  onImageGenerated: (imageUrl: string) => void;
}

interface GeneratedImage {
  url: string;
  format: string;
  style: string;
}

const moodTemplates = [
  { id: 'elegant', name: 'Elegant & Formal', prompt: 'elegant, sophisticated, formal, luxurious, classy, gold accents, premium design' },
  { id: 'fun', name: 'Fun & Playful', prompt: 'fun, playful, colorful, vibrant, energetic, cheerful, confetti, celebration' },
  { id: 'casual', name: 'Casual & Relaxed', prompt: 'casual, relaxed, friendly, warm, cozy, comfortable, approachable' },
  { id: 'professional', name: 'Professional & Corporate', prompt: 'professional, corporate, clean, modern, business, minimalist, structured' },
  { id: 'romantic', name: 'Romantic & Intimate', prompt: 'romantic, intimate, soft, dreamy, gentle, warm lighting, pastel colors' },
  { id: 'outdoor', name: 'Outdoor & Nature', prompt: 'outdoor, nature, fresh, natural, green, scenic, botanical, organic' },
  { id: 'celebration', name: 'Celebration & Party', prompt: 'celebration, party, festive, joyful, confetti, balloons, sparklers' },
  { id: 'minimalist', name: 'Minimalist & Clean', prompt: 'minimalist, clean, simple, modern, geometric, white space, typography-focused' },
];

const formats = [
  { id: 'square', name: 'Square (1:1)', dimensions: '1024x1024', description: 'Perfect for social media' },
  { id: 'landscape', name: 'Landscape (16:9)', dimensions: '1536x864', description: 'Great for email headers' },
  { id: 'portrait', name: 'Portrait (4:5)', dimensions: '1024x1280', description: 'Ideal for invitations' },
];

interface EventDetails {
  eventName: string;
  eventType: string;
  description?: string;
  eventDate?: string;
  eventTime?: string;
  eventLocation?: string;
  hostName?: string;
  deadline?: string;
}

export const AIImageGenerator: React.FC<AIImageGeneratorProps & EventDetails> = ({
  eventName,
  eventType,
  description,
  eventDate,
  eventTime,
  eventLocation,
  hostName,
  deadline,
  onImageGenerated
}) => {
  const [selectedMood, setSelectedMood] = useState<string>('');
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<string>('square');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  
  
  const { subscribed, createCheckout } = useSubscription();

  const formatEventDetails = () => {
    const details = [];
    if (eventDate) details.push(`Date: ${eventDate}`);
    if (eventTime) details.push(`Time: ${eventTime}`);
    if (eventLocation) details.push(`Location: ${eventLocation}`);
    if (hostName) details.push(`Host: ${hostName}`);
    return details.join(' | ');
  };

  const generateImage = async () => {
    if (!selectedMood && !customPrompt.trim()) {
      toast.error('Please select a mood or enter a custom prompt');
      return;
    }

    setIsGenerating(true);
    
    try {
      const selectedMoodTemplate = moodTemplates.find(m => m.id === selectedMood);
      const moodPrompt = useCustomPrompt ? customPrompt : selectedMoodTemplate?.prompt || '';
      
      // Enhanced prompt focusing on decorative background only
      const fullPrompt = `Create a professional decorative background for "${eventName}" event invitation. 
      Style: ${moodPrompt}. 
      ${description ? `Event context: ${description}.` : ''} 
      
       CRITICAL LAYOUT REQUIREMENTS:
       - Create ONLY a decorative background pattern or design
       - NO TEXT, NO DATES, NO EVENT DETAILS whatsoever
       - Leave ample clear space throughout for text overlays
       - Use elegant colors and patterns that provide good contrast for overlaid text
       - Professional invitation design with subtle decorative elements
       - Background should be sophisticated but not overwhelming
       - Ensure text readability zones are maintained
       - NO TYPOGRAPHY OR TEXT ELEMENTS AT ALL
       
       This will be a base layer for event information to be added programmatically.`;

      const format = formats.find(f => f.id === selectedFormat)!;
      const [width, height] = format.dimensions.split('x').map(Number);

      console.log('Generating AI background image with prompt:', fullPrompt);

      // Use Supabase client instead of direct fetch
      const { data, error } = await supabase.functions.invoke('generate-invitation-image', {
        body: {
          prompt: fullPrompt,
          width,
          height,
        },
      });

      if (error) {
        console.error('Error generating image:', error);
        throw new Error(error.message || 'Failed to generate image');
      }

      if (data.error) {
        throw new Error(data.error);
      }

      const newImage: GeneratedImage = {
        url: data.output[0],
        format: selectedFormat,
        style: selectedMoodTemplate?.name || 'Custom',
      };

      console.log('AI image generated successfully:', newImage.url);
      setGeneratedImages(prev => [newImage, ...prev]);
      toast.success('Background image generated successfully!');
      
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      // Replace with your actual Stripe price ID for Pro subscription
      await createCheckout('price_1RmCz1AX6lBz9Nrz0Vas9kOP');
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to start checkout process');
    }
  };

  const handleImageSelect = (imageUrl: string) => {
    console.log('Selected AI-generated background image:', imageUrl);
    onImageGenerated(imageUrl);
    toast.success('Background image selected! Event details and QR code will be added automatically.');
  };

  // Show Pro-only message for free users
  if (!subscribed) {
    return (
      <div className="space-y-6">
        <Card className="relative">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI-Generated Invitation Design
              <Badge variant="outline" className="ml-auto flex items-center gap-1">
                <Crown className="h-3 w-3" />
                Pro Feature
              </Badge>
            </CardTitle>
            <CardDescription>
              Create beautiful, unique invitation images for "{eventName}" using AI
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Blurred preview content */}
            <div className="relative">
              <div className="filter blur-sm pointer-events-none">
                {/* Style Selection */}
                <div className="space-y-3">
                  <Label>Choose Style Approach</Label>
                  <div className="flex gap-4">
                    <Button variant="outline" className="flex-1">
                      Mood Templates
                    </Button>
                    <Button variant="outline" className="flex-1">
                      Custom Prompt
                    </Button>
                  </div>
                </div>

                {/* Sample mood templates */}
                <div className="space-y-3">
                  <Label>Select Mood</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {moodTemplates.slice(0, 4).map((mood) => (
                      <Button
                        key={mood.id}
                        variant="outline"
                        className="h-auto p-3 text-left"
                        disabled
                      >
                        <div>
                          <div className="font-medium">{mood.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {mood.prompt.split(',').slice(0, 3).join(', ')}...
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Disabled generate button */}
                <Button disabled className="w-full" size="lg">
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate AI Invitation
                </Button>
              </div>

              {/* Pro upgrade overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/50 to-transparent flex items-center justify-center">
                <div className="text-center space-y-4 p-6 bg-background/95 rounded-lg border shadow-lg max-w-sm">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto bg-primary/10 rounded-full">
                    <Lock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">AI Image Generation</h3>
                    <p className="text-muted-foreground text-sm mt-1">
                      Create stunning, personalized invitation images with our AI-powered design tool
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground">
                      ✨ Multiple design styles & moods
                    </div>
                    <div className="text-sm text-muted-foreground">
                      🎨 Custom prompts & formats
                    </div>
                    <div className="text-sm text-muted-foreground">
                      📱 High-quality downloadable images
                    </div>
                  </div>
                  <Button onClick={handleUpgrade} className="w-full">
                    <Crown className="mr-2 h-4 w-4" />
                    Upgrade to Pro
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI-Generated Invitation Background
            <Badge variant="default" className="ml-auto flex items-center gap-1 bg-gradient-primary">
              <Crown className="h-3 w-3" />
              Pro
            </Badge>
          </CardTitle>
          <CardDescription>
            Generate a decorative background for "{eventName}" - event details and QR code will be added automatically
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Style Selection */}
          <div className="space-y-3">
            <Label>Choose Style Approach</Label>
            <div className="flex gap-4">
              <Button
                variant={!useCustomPrompt ? "default" : "outline"}
                onClick={() => setUseCustomPrompt(false)}
                className="flex-1"
              >
                Mood Templates
              </Button>
              <Button
                variant={useCustomPrompt ? "default" : "outline"}
                onClick={() => setUseCustomPrompt(true)}
                className="flex-1"
              >
                Custom Prompt
              </Button>
            </div>
          </div>

          {/* Mood Templates */}
          {!useCustomPrompt && (
            <div className="space-y-3">
              <Label>Select Mood</Label>
              <div className="grid grid-cols-2 gap-2">
                {moodTemplates.map((mood) => (
                  <Button
                    key={mood.id}
                    variant={selectedMood === mood.id ? "default" : "outline"}
                    onClick={() => setSelectedMood(mood.id)}
                    className="h-auto p-3 text-left"
                  >
                    <div>
                      <div className="font-medium">{mood.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {mood.prompt.split(',').slice(0, 3).join(', ')}...
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Custom Prompt */}
          {useCustomPrompt && (
            <div className="space-y-3">
              <Label htmlFor="custom-prompt">Custom Style Description</Label>
              <Input
                id="custom-prompt"
                placeholder="Describe the style you want (e.g., 'modern, minimalist, blue and white colors')"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
              />
            </div>
          )}

          {/* Format Selection */}
          <div className="space-y-3">
            <Label>Image Format</Label>
            <Select value={selectedFormat} onValueChange={setSelectedFormat}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {formats.map((format) => (
                  <SelectItem key={format.id} value={format.id}>
                    <div>
                      <div className="font-medium">{format.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {format.description}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Generate Button */}
          <Button
            onClick={generateImage}
            disabled={isGenerating || (!selectedMood && !customPrompt.trim())}
            className="w-full"
            size="lg"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating Background Design...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Background Design
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Generated Images */}
      {generatedImages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Generated Background Designs</CardTitle>
          <CardDescription>
            Choose your favorite background - event details and QR code will be added automatically
          </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {generatedImages.map((image, index) => (
                <div key={index} className="space-y-3">
                  <div className="relative group">
                    <img
                      src={image.url}
                      alt={`Generated background ${index + 1}`}
                      className="w-full rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleImageSelect(image.url)}
                        >
                          Select Background
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => window.open(image.url, '_blank')}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary">{image.style}</Badge>
                    <Badge variant="outline">{formats.find(f => f.id === image.format)?.name}</Badge>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t">
              <Button
                variant="outline"
                onClick={generateImage}
                disabled={isGenerating}
                className="w-full"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Generate Another Background
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};
